<template>
  <div class="cel-elements">
    <h1 class="text-xl text-bold">Why Us?</h1>

    <div>
      At Cel-Elements, we are deeply committed to offering skin care products that meet the highest standards of
      quality, safety, and ethical responsibility. Our dedication is reflected through our rigorous adherence to
      international guidelines and certifications. Here's why Cel-Elements stands out:
    </div>

    <h2 class="text-lg mt-4">{{ title }}</h2>

    <section>
      <h2 class="text-lg text-bold">Raw Materials Quality</h2>
      <ul>
        <li v-for="(cert, index) in rawMaterialsQuality" :key="index">
          <h3>{{ cert.name }}</h3>
          <div class="flex gap-2">
            <img :src="cert.img" :alt="cert.name" style="width: 80px;">
            <p>{{ cert.description }}</p>
          </div>
        </li>
      </ul>
    </section>

    <section>
      <h2 class="text-lg text-bold">Natural & Organic Standards</h2>
      <ul>
        <li v-for="(cert, index) in organicCertifications" :key="index">
          <h3>{{ cert.name }}</h3>

          <div class="flex gap-2" v-if="Array.isArray(cert.img)">
            <div :key="index" style="width: 80px;">
              <img :src="img" :alt="`${cert.name}[${index}]`" v-for="(img, index) in cert.img">
            </div>
            <p>{{ cert.description }}</p>
          </div>
          <div v-else>
            <img :src="cert.img" :alt="cert.name" style="width: 80px;">
            <p>{{ cert.description }}</p>
          </div>
        </li>
      </ul>
    </section>

    <section>
      <h2 class="text-lg text-bold">Compliance & Commitment</h2>
      <ul>
        <li v-for="(cert, index) in complianceCertifications" :key="index">
          <h3>{{ cert.name }}</h3>
          <div class="flex gap-2">
            <img :src="cert.img" :alt="cert.name" style="width: 80px;">
            <p>{{ cert.description }}</p>
          </div>
        </li>
      </ul>
    </section>

    <section>
      <h2 class="text-lg text-bold">Great Effects Speak Louder Than Words</h2>
      <div class="mb-6">
        At Cel-Elements, we’re all about pioneering skincare innovations that are naturally derived, safe, and super effective. We firmly believe that results are everything. Whether you’re dealing with acne-prone skin or mature skin, our diverse range of products tackles all skin concerns effortlessly. Achieving radiant confidence and slaying your look has never been easier. Stay glowing and outshine everyone with ease!

        <div class="flex gap-2 my-4 w-full h-[250px] md:h-[400px]">
          <div class="h-full bg-cover bg-center w-1/2" style="background-image: url('/img/whyus/image015.jpg')"></div>
          <div class="h-full bg-cover bg-center w-1/2" style="background-image: url('/img/whyus/image016.jpg')"></div>
        </div>
      </div>

      <div class="mb-6">
        One of our customers from Indonesia struggled with acne before discovering Cel-Elements. With consistent skin treatment and the use of our home-care products, she regained healthy skin and newfound confidence! 
        <div class="flex gap-2 my-4 w-full h-[250px] md:h-[400px]">
          <div class="h-full flex-1 bg-cover w-1/2" style="background-image: url('/img/whyus/image018.jpg')"></div>
          <div class="h-full flex-1 bg-cover w-1/2" style="background-image: url('/img/whyus/image019.jpg')"></div>
        </div>
      </div>

      <div class="mb-6">
        Her skin concern was one of the most challenging we had ever encountered. Due to her condition, she suffered from severe pustules that caused her significant pain and distress. Our partnered beautician put in tremendous effort to address her skin issues using CE skin treatment sets and homecare products. Fortunately, her skin concerns were brought under control, and she no longer had to endure pain, blood, and pus.
        <div class="flex gap-2 my-4 mx-auto w-full max-w-[800px] h-[480px]">
          <div class="h-full flex-1 bg-cover w-1/2" style="background-image: url('/img/whyus/image020.jpg')"></div>
          <div class="h-full flex-1 bg-cover w-1/2" style="background-image: url('/img/whyus/image021.jpg')"></div>
        </div>
      </div>

      <div class="mb-6">
        Spots, redness, freckles, melasma, and skin pigmentation can be quite bothersome. Fortunately, with the convenience of CE products, customers can harness the power of natural plant extracts to achieve a fairer, more radiant, and nourished complexion.
        <div class="flex flex-col md:flex-row gap-2 my-4 w-full">
          <div class="h-[300px] md:h-[480px] bg-cover w-full md:w-1/2" style="background-image: url('/img/whyus/image022.jpg')"></div>
          <div class="h-[300px] md:h-[480px] bg-cover w-full md:w-1/2" style="background-image: url('/img/whyus/image023.jpg')"></div>
        </div>
      </div>

      <div class="mb-6">
        Conquering the signs of aging has long been a goal for many. Recognizing the need to combat aging skin, our overseas partners’ research and development team has been creating cutting-edge ingredients and technologies in skincare products to meet this demand. Staying young and youthful is no longer just a dream.
        <div class="flex gap-2 my-4">
          
        </div>
      </div>
    </section>

    <section class="testimonials">
      <h2>Our Testimonials</h2>
      <p>{{ testimonialIntro }}</p>
      <div v-for="(testimonial, index) in testimonials" :key="index" class="testimonial">
        <blockquote>
          <div class="flex flex-col md:flex-row gap-2">
            <img :src="testimonial.img" :alt="testimonial.author" class="w-full md:w-1/2">
            <div class="w-full md:w-1/2">
              <p>{{ testimonial.quote }}</p>
              <footer>- {{ testimonial.author }}</footer>
            </div>
          </div>
        </blockquote>
      </div>
    </section>
  </div>
</template>
<script setup lang="ts">
import { ref } from 'vue'

const title = ref('Cel-Elements: Certified Quality Skincare')

const rawMaterialsQuality = ref([
  {
    name: 'Non-GMO Ingredients',
    description: 'We prioritize the use of non-genetically modified ingredients, ensuring that our products are natural and free from genetic modifications.',
    img: "/img/whyus/image001.png",
  },
  {
    name: 'Phthalate-Free',
    description: 'Our products are free from phthalates, a type of plasticizer that can be harmful to your health, ensuring that what you apply to your skin is pure and safe.',
    img: "/img/whyus/image002.png",
  },
  {
    name: 'Hormone-Free',
    description: 'Cel-Elements products do not contain any added hormones or estrogen-mimicking chemicals, safeguarding your body from unwanted hormonal disruptions.',
    img: "/img/whyus/image003.png",
  },
  {
    name: 'Cruelty-Free',
    description: 'We are committed to cruelty-free practices, meaning that none of our products are tested on animals. Your skincare choice with us is also a compassionate one.',
    img: "/img/whyus/image004.png",
  },
  {
    name: 'No Radioactive Pollution',
    description: 'We ensure that our raw materials are sourced from regions free from radioactive pollution, providing you with the safest skincare options.',
    img: "/img/whyus/image005.png",
  }
])

const complianceCertifications = ref([
  {
    name: 'ISO 22716 GMP for Cosmetics',
    description: 'Our manufacturing processes are certified by ISO 22716, which ensures that all our skincare products are produced according to Good Manufacturing Practices (GMP). This standard emphasizes safety and quality control in the cosmetic industry.',
    img: "/img/whyus/image010.png",
  },
  {
    name: 'FDA Compliance',
    description: 'We adhere to the stringent regulations set by the Food & Drug Administration (FDA), ensuring that our products are safe for use and meet the required quality standards.',
    img: "/img/whyus/image011.png",
  },
  {
    name: 'Taiwan HALAL Certification',
    description: 'Cel-Elements is proud to offer products that are certified by the Taiwan HALAL Integrity Development Association. This certification assures that our products meet the dietary requirements for Halal, reflecting our commitment to inclusivity.',
    img: "/img/whyus/image012.png",
  },
  {
    name: 'Good Manufacturing Practices (GMP)',
    description: 'Beyond cosmetics, we also follow the GMP standards for pharmaceutical-grade products, ensuring that every product you use is crafted with the utmost care.',
    img: "/img/whyus/image014.png",
  }
])

const organicCertifications = ref([
  {
    name: 'ECOCERT Certification',
    description: 'We hold the Ecocert Greenlife SAS certification, confirming that our products are made with ingredients that are environmentally friendly and sustainably sourced.',
    img: "/img/whyus/image006.png",
  },
  {
    name: 'Australian Certified Organic',
    description: 'Our commitment to organic skincare is further evidenced by our Australian Certified Organic certification, which guarantees that our products are free from harmful chemicals and synthetic substances.',
    img: "/img/whyus/image007.png",
  },
  {
    name: 'BDIH (Germany) & Cosmebio (France)',
    description: 'We adhere to the strict organic and natural cosmetic standards set by BDIH in Germany and Cosmebio in France, ensuring that our products are both safe for you and respectful of the environment.',
    img: [
      "/img/whyus/image008.png",
      "/img/whyus/image009.png"
    ],
  }
])

const testimonialIntro = ref('At Cel-Elements, our greatest pride comes from the trust and satisfaction of our customers. We believe that the true measure of our success lies in the positive experiences of those who use our products. Below, you\'ll find genuine testimonials from our valued customers who have experienced the transformative power of our skincare. Their words reflect the quality, effectiveness, and care that goes into every product we create.')

const testimonials = ref([
  {
    quote: "Well-deserved! Whether it's the company, products, or services, they are all trustworthy. Congratulations to Cel-Elements!",
    author: "Coco (Beautician)",
    img: "/img/whyus/image024.jpg",
  },
  {
    quote: "Congratulations to Cel-Elements for receiving government recognition! I’ve always been grateful for Cel-Elements’ products, which have helped my sensitive, red skin return to normal. Especially their Stabilizing Serum, which has effectively controlled my skin condition. If you’re unsure about which products to use, they also have professional beauticians available for consultations to help you find the right products. Highly recommended!",
    author: "Beatrice",
    img: "/img/whyus/image025.jpg",
  },
  {
    quote: "After using Cel-Elements for twenty years and being taken good care of by Ms. Koo & Teresa, my facial skin is gradually getting nourished, healthy, and energetic. Extremely satisfied and thankful to the CE team",
    author: "Sandy",
    img: "/img/whyus/image026.jpg",
  },

  {
    quote: "I am an Indonesian customer who has been using Cel-Elements for a year and a half. My face used to be covered with acne, but now people compliment me on my smooth and delicate skin. I’m really happy! Using Cel-Elements has given me a lot of confidence. The acne that troubled me for years is gone, and my spots are gradually fading. I’m truly delighted. Thank you, Go Biguan, for introducing us to Cel-Elements. Let’s all be beautiful together!",
    author: "Ahun Lifen",
    img: "/img/whyus/image027.jpg",
  },

  {
    quote: "Thank you, Cel-Elements, for helping me control and improve my acne. My top recommendation is Sebo Defense because whenever I have inflammation, applying a thicker layer of Sebo Defense gradually reduces it. The results are quite noticeable. Congratulations to Cel-Elements on this honor!",
    author: "Zhen",
    img: "/img/whyus/image028.jpg",
  },

  {
    quote: "",
    author: "Jia Wei",
    img: "/img/whyus/image029.jpg",
  },
  {
    quote: "Thank you, Cel-Elements, for helping treat my face even though I am a customer from Indonesia. I am very satisfied with Cel-Elements because I meet clients for work, and my face is full of problems, making me less confident. After being introduced to Cel-Elements, I am very happy because my facial issues like dark spots, acne, and wrinkles have been treated. Now my face looks fresher, and I feel more confident. Thank you, Cel-Elements! Love you lots",
    author: "Hwang Mei Mei",
    img: "/img/whyus/image030.jpg",
  },
  {
    quote: "I love the 7C series masks. They are not only super moisturizing but also have a whitening effect and are extra-large. After using them, my whole face feels hydrated and smooth. Whenever I return from a long trip, I use them daily. They are perfect for busy people who still want to maintain beautiful skin. Don’t hesitate to give Cel-Elements a try—it can help you too! Thank you, Cel-Elements, you are trustworthy.",
    author: "Gee Hong Cheng",
    img: "/img/whyus/image031.jpg",
  },
  {
    quote: "",
    author: "Jia Sheng Gee",
    img: "/img/whyus/image032.jpg",
  },
  {
    quote: "In the ever-evolving beauty industry, Cel-Elements’ behind-the-scenes heroes continuously innovate to find and create better quality management, ensuring that every CE user achieves healthy skin. Most importantly, the passion and interaction style of the Cel-Elements team makes everyone feel warm and comfortable. Leng Beauty Care has trusted this brand since its inception. We look forward to Cel-Elements reaching new heights in the beauty industry!",
    author: "Patricia (Beautician)",
    img: "/img/whyus/image033.jpg",
  },
  {
    quote: "Cel-Elements is the brand I trust the most. It not only helps me solve various skin problems, but the results are also amazing. It has made me more confident to stand out. Grateful to have you. Thank you. ",
    author: "Lolly Pop",
    img: "/img/whyus/image034.jpg",
  },
  {
    quote: "",
    author: "Jonathan",
    img: "/img/whyus/image035.jpg",
  },
  {
    quote: "My friends always ask me which products I have been using, thus my skin is so smooth. Although the products here are quite pricey, they are worthwhile.",
    author: "Shirley",
    img: "/img/whyus/image036.jpg",
  }
])
</script>

<style scoped>
/* Add your styles here */
.cel-elements {
  max-width: 1100px;
  margin: 0 auto;
  padding: 20px;
}

h1,
h2 {
  color: #333;
}

ul {
  list-style-type: none;
  padding: 0;
}

li {
  margin-bottom: 20px;
}

.testimonial {
  background-color: #f9f9f9;
  border-left: 4px solid #ddd;
  margin-bottom: 20px;
  padding: 10px 20px;
}

blockquote {
  font-style: italic;
}

blockquote footer {
  font-style: normal;
  text-align: right;
}
</style>
